import { Component, Input, OnInit } from "@angular/core";
import { cloneDeep } from "lodash";
import { finalize, tap } from "rxjs/operators";
import { GroupAttrService } from "../../models/group-attr.service";
import { ProjectAttrService } from "../../models/project-attr.service";
import { PlanTierService } from "../../plan-tier/plan-tier.service";
import { StdAttrsService } from "../../std-attrs/std-attrs.service";
import { UpgradeModalService } from "../../upgrade-modal/upgrade-modal.service";

@Component({
  selector: "ab-attr-list",
  templateUrl: "./attr-list.component.html",
})
export class AttrListComponent implements OnInit {
  @Input() public project: any;
  @Input() public group: any;

  public attrs: any;
  public features: any;
  public loading: boolean;

  constructor(
    private groupAttrService: GroupAttrService,
    private planTierService: PlanTierService,
    private projectAttrService: ProjectAttrService,
    private stdAttrsService: StdAttrsService,
    private upgradeModal: UpgradeModalService,
  ) {}

  public onCreateAttr($event: any) {
    // tslint:disable-next-line:no-shadowed-variable
    for (const attr of this.attrs) {
      if (attr.key === $event.attrKey) {
        return;
      }
    }

    const attr = this.customAttr($event.attrKey);
    this.attrs.push(attr);
    this.loadAttr(attr);

    if ($event.projectAttr) {
      this.project.attributes.push($event.attrKey);
      this.project = cloneDeep(this.project);
      return;
    }

    this.group.attributes.push($event.attrKey);
    return (this.group = cloneDeep(this.group));
  }

  public deleteAttr(attr: any) {
    let params;
    let ind = this.project.attributes.indexOf(attr.key);
    if (ind !== -1) {
      params = {
        projectId: this.project.id,
        attribute: attr.key,
      };

      this.projectAttrService.delete(params).subscribe(() => {
        this.removeCustomAttr(attr.key);

        this.project.attributes.splice(ind, 1);
        this.project = cloneDeep(this.project);
      });

      return;
    }

    ind = this.group.attributes.indexOf(attr.key);
    if (ind !== -1) {
      params = {
        projectId: this.group.projectId,
        groupId: this.group.id,
        attribute: attr.key,
      };

      return this.groupAttrService.delete(params).subscribe(() => {
        this.removeCustomAttr(attr.key);

        this.group.attributes.splice(ind, 1);
        return (this.group = cloneDeep(this.group));
      });
    }
  }

  public openUpgradeModal($event: any, toTier?: any) {
    return this.upgradeModal.open($event, toTier);
  }

  public ngOnInit() {
    const attrs = cloneDeep(this.stdAttrsService.getAttrs());
    for (const attr of attrs) {
      if (attr.key === "errors.0.file") {
        attr.key = "errors.0.stackRecord";
        break;
      }
    }

    if (!this.project.attributes) {
      this.project.attributes = [];
    }
    for (const attrKey of this.project.attributes) {
      attrs.push(this.customAttr(attrKey));
    }

    if (!this.group.attributes) {
      this.group.attributes = [];
    }
    for (const attrKey of this.group.attributes) {
      attrs.push(this.customAttr(attrKey));
    }

    for (const attr of attrs) {
      // `basic` & `custom` flags determine how to group the page output:
      // basic, advanced & custom.
      attr.basic = this.planTierService.isBasicAttr(attr);
      attr.custom = !!attr.custom;
      this.loadAttr(attr);
    }

    this.loading = true;
    this.planTierService
      .features()
      .pipe(
        tap((features: any) => {
          this.features = features;
          // Disable aggregations that are not in the plan's tier.
          for (const attr of attrs) {
            attr.featureDisabled = !this.planTierService.allowsAggregationAttr(
              attr,
              this.features,
            );
          }
          this.attrs = attrs;
        }),
      )
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe();
  }

  public filterAttrs(filterTerm: any) {
    const filterKeys = Object.keys(filterTerm);
    return this.attrs.filter((attr: any) => {
      const notMatched = filterKeys.find((key: string) => {
        return attr[key] !== filterTerm[key];
      });
      return !notMatched;
    });
  }

  private customAttr(attrKey: string) {
    return {
      key: attrKey,
      heading: attrKey,
      custom: true,
    };
  }

  private removeCustomAttr(attrKey: string) {
    for (let i = 0; i < this.attrs.length; i++) {
      const attr = this.attrs[i];
      if (attr.key === attrKey) {
        this.attrs.splice(i, 1);
        break;
      }
    }
  }

  private loadAttr(attr: any) {
    attr.column = "notice_count";
    attr.order = "DESC";
    attr.params = {
      projectId: this.group.projectId,
      groupId: this.group.id,
      attribute: attr.key,
      limit: 10,
      page: 1,
    };

    attr.loadNextPage = () => {
      attr.loading = true;
      return this.groupAttrService
        .attrs(attr.params)
        .pipe(
          finalize(() => {
            attr.loading = false;
          }),
        )
        .subscribe((data: any) => (attr.data = data));
    };

    attr.load = (column: string) => {
      if (column != null) {
        attr.column = column;
        attr.order = attr.order === "DESC" ? "ASC" : "DESC";
        attr.sorting = true;
      } else {
        attr.loading = true;
      }

      attr.params.order = `${attr.column} ${attr.order}`;
      this.groupAttrService
        .attrs(attr.params)
        .pipe(tap((data: any) => (attr.data = data)))
        .pipe(
          finalize(() => {
            attr.loading = false;
            attr.sorting = false;
          }),
        )
        .subscribe();
    };

    attr.load();
  }
}
